import React, { createContext, useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import delay from 'delay'
import hipersendService from '../../services/hipersend'
import { suspensaoDaResposta, mesmaSuspensao } from '../../libs/suspensaoOficial'

const ApiDataWrapperContext = createContext()

const ApiDataWrapperProvider = ({ children }) => {
  const [queryParameters] = useSearchParams()
  const [server, setServer] = useState('')
  const [isSuspended, setIsSuspended] = useState(undefined)
  const [isDisabled, setIsDisabled] = useState(false)
  const [provider, setProvider] = useState('')
  const [phone, setPhone] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [whatsappBusinessId, setWhatsappBusinessId] = useState('')
  const [whatsappBusinessNumberId, setWhatsappBusinessNumberId] = useState('')
  const [whatsappBusinessToken, setWhatsappBusinessToken] = useState('')
  const [statusSession, setStatusSession] = useState('')
  const [user, setUser] = useState(undefined)
  const [verifiedName, setVerifiedName] = useState('')
  // Flag por instância (definida pelo operador): controla se o "saldo estimado"
  // aparece pro cliente. Só faz sentido pra quem usa a nossa linha de crédito.
  const [usaLinhaDeCredito, setUsaLinhaDeCredito] = useState(false)
  // Saldo pré-pago REAL vindo do backend: { consumoMsgs, gasto, recargas, saldo } ou
  // null (não-cobrável). precoMsgPacote = preço/msg do pacote contratado (ou null).
  const [saldoInfo, setSaldoInfo] = useState(null)
  const [precoMsgPacote, setPrecoMsgPacote] = useState(null)
  // Regime comercial da cobrança ('linhaCompleta' | 'taxaServico' | null) —
  // metadado de rótulo vindo do backend; null com master antigo.
  const [modoCobranca, setModoCobranca] = useState(null)
  // Suspensão do WhatsApp OFICIAL da carteira ({ motivo, desde, recado } ou
  // null) e o link de recarga que o servidor manda. É outra coisa que a API
  // suspensa (isSuspended): aqui só o envio pelo oficial no GLA Conecta para.
  const [suspensaoOficial, setSuspensaoOficial] = useState(null)
  const [linkRecarga, setLinkRecarga] = useState(null)

  // Preço/msg EFETIVO pras estimativas do painel (tetos/economia do SmartFilter):
  // com preços por categoria no contrato, vale o Utility VIGENTE (a régua que a
  // UI usa); senão o precoMsg do pacote, como sempre. ZERO explícito é isenção
  // válida — por isso a checagem é de presença, nunca truthiness.
  const precoEfetivo = (cobranca) => {
    if (!cobranca) return null
    const util = cobranca.precos ? cobranca.precos.UTILITY : undefined
    if (util !== undefined && util !== null) return util
    return cobranca.precoMsg || null
  }

  // Leva a suspensão do oficial e o link de recarga de uma resposta da consulta
  // de informações para o contexto (é dele que a faixa do layout lê). Usada no
  // boot, no refreshSaldo e no polling da tela inicial — assim a recarga que
  // reativa tira a faixa sem recarregar a página, e uma suspensão nova aparece,
  // sem nenhuma consulta a mais. Só a resposta de SUCESSO mexe na faixa: o
  // master responde alguns erros com HTTP 200 (instância não achada vem com
  // result "error"), e isso não pode apagar uma suspensão que continua valendo.
  const aplicarSuspensao = useCallback((response) => {
    if (!response || typeof response !== 'object' || response.result !== 'success') return
    const nova = suspensaoDaResposta(response)
    setSuspensaoOficial((atual) => (mesmaSuspensao(atual, nova) ? atual : nova))
    setLinkRecarga(response.linkRecarga || null)
  }, [])

  const getCredentials = () => {
    const api = queryParameters.get('api') || window.localStorage.getItem('api')
    const secret = queryParameters.get('secret') || window.localStorage.getItem('secret')

    return { api, secret }
  }

  const fetchData = async () => {
    try {

      const { api, secret } = getCredentials()
      if(!api || !secret) {
        return
      }
      if(phone && server && isSuspended) {
        return
      }

      setIsLoading(true)
      await delay(1000)

      const response = await hipersendService.notificacoesInteligentes.getInformations(api, secret)

      setServer(response.numberServer)
      setIsSuspended(response.isSuspended)
      setIsDisabled(response.isDisabled)

      const isMetaProvider = response.provider === 'hiperapi-v3-cloud-official'
      const metaDisplayPhone = response.whatsappCloudApiDisplayPhone
      const resolvedPhone = isMetaProvider && metaDisplayPhone
        ? metaDisplayPhone.replace(/^\+/, '').replace(/\D/g, '')
        : response.info.me.user
      setPhone(resolvedPhone)

      setProvider(response.provider)
      setWhatsappBusinessId(response.whatsappCloudApiBusinessId)
      setWhatsappBusinessNumberId(response.whatsappCloudApiNumberId)
      setWhatsappBusinessToken(response.whatsappCloudApiToken)
      setStatusSession(response.statusSession)
      setUser(response.user)
      setUsaLinhaDeCredito(!!response.usaLinhaDeCredito)
      setSaldoInfo(response.saldo || null)
      setPrecoMsgPacote(precoEfetivo(response.cobranca))
      setModoCobranca(response.modoCobranca || null)
      aplicarSuspensao(response)
      setVerifiedName(response.whatsappCloudApiVerifiedName || '')
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  // Re-busca SÓ o saldo (sem refazer o boot inteiro nem o delay), pra atualizar
  // depois de envios/recargas quando o cliente clica "Atualizar" ou troca de aba —
  // o saldo muda com o consumo. Falha aqui não atrapalha o resto do painel.
  const refreshSaldo = useCallback(async () => {
    try {
      const api = queryParameters.get('api') || window.localStorage.getItem('api')
      const secret = queryParameters.get('secret') || window.localStorage.getItem('secret')
      if (!api || !secret) return
      const response = await hipersendService.notificacoesInteligentes.getInformations(api, secret)
      setSaldoInfo(response.saldo || null)
      setPrecoMsgPacote(precoEfetivo(response.cobranca))
      setModoCobranca(response.modoCobranca || null)
      // A suspensão sai sozinha com a recarga: quem atualiza o saldo vê a
      // faixa sumir junto.
      aplicarSuspensao(response)
    } catch (error) {
      console.error(error)
    }
  }, [queryParameters, aplicarSuspensao])

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <ApiDataWrapperContext.Provider
      value={{
        getCredentials,
        isSuspended,
        setIsSuspended,
        isDisabled,
        setIsDisabled,
        server,
        setServer,
        phone,
        setPhone,
        provider,
        setProvider,
        fetchData,
        isLoading,
        setIsLoading,
        whatsappBusinessId,
        setWhatsappBusinessId,
        whatsappBusinessNumberId,
        setWhatsappBusinessNumberId,
        whatsappBusinessToken,
        setWhatsappBusinessToken,
        setStatusSession,
        statusSession,
        setUser,
        user,
        usaLinhaDeCredito,
        saldoInfo,
        precoMsgPacote,
        modoCobranca,
        suspensaoOficial,
        linkRecarga,
        aplicarSuspensao,
        refreshSaldo,
        verifiedName,
        setVerifiedName
      }}
    >
      {children}
    </ApiDataWrapperContext.Provider>
  )
}

export {
  ApiDataWrapperContext,
  ApiDataWrapperProvider
}
